// Single source of truth for white-label branding. To rebrand for a new club,
// edit this file. See README-WHITELABEL.md in the project root for the full
// 30-minute setup checklist.
import { FOURBALL_ONLY, PBCC_PREMIUM, HUSTLER, IS_GOLF_ELITE } from './siteMode.js'
import { GOLF_ELITE as GE } from './brand.js'

const CLUB = {
  // Club identity (Golf Hustler build drops the PBCC identity entirely; Golf
  // Elite — the platform default brand — pulls everything from brand.js).
  name: IS_GOLF_ELITE ? GE.name : (HUSTLER ? 'Golf Hustler' : 'Pine Bluff Country Club'),
  shortName: IS_GOLF_ELITE ? GE.shortName : (HUSTLER ? 'Golf Hustler' : 'PBCC'),

  // Branding — the ONE correct logo: green pine-tree wordmark (PINE BLUFF
  // COUNTRY CLUB / EST 1914). It's forest-green (#123D26) on transparent:
  //   logo      = green version → use on LIGHT/cream/white surfaces
  //   logoLight = white version → use on DARK-green surfaces (headers, boards)
  // so it never vanishes. Pick the right one per background at each usage site.
  logo: IS_GOLF_ELITE ? GE.logo : '/pbcc-pine-green.png',       // green pine — LIGHT/white surfaces
  logoLight: IS_GOLF_ELITE ? GE.logo : '/pbcc-pine-white.png',  // white pine — DARK-green surfaces
  // Golf Hustler: near-black surfaces + near-black text on white cards (lime is
  // too low-contrast for body text on white). Lime/gold are brand accents only.
  primaryColor: IS_GOLF_ELITE ? GE.primary : (HUSTLER ? '#0A0A0A' : '#123D26'), // forest green — matches the logo (was near-black #123D26)
  // 4-Ball brand: NO gold. The config accent becomes WHITE (it sits on dark-green
  // surfaces app-wide); full builds keep gold. Hardcoded golds elsewhere were
  // swept to white (dark boards) / green (white cards) in this pass.
  // 4-Ball → white accent (on dark-green). The FULL premium build (yesnetlabs)
  // uses the premium GOLD so the games/Pro-Shop pages match the 4-Ball theme.
  // Non-premium full builds keep the original amber.
  accentColor: IS_GOLF_ELITE ? GE.accent : (HUSTLER ? '#F7C601' : (FOURBALL_ONLY ? '#FFFFFF' : (PBCC_PREMIUM ? '#C2A24E' : '#E19E0E'))),
  accentColorDark: IS_GOLF_ELITE ? GE.accentDark : (HUSTLER ? '#C99E00' : (FOURBALL_ONLY ? '#FFFFFF' : (PBCC_PREMIUM ? '#9C7B2E' : '#BB830C'))),
  // Page-level background — set to the "deep canopy" green (darker than
  // primary). All non-locked public pages inherit this via CLUB.backgroundColor
  // so they match the landing body + access-code page + admin sidebar.
  // Golf Elite keeps the SAME light-content / dark-chrome structure as PBCC
  // (so every existing contrast relationship holds — white text stays on dark
  // surfaces), just navy instead of green: the big app background is navy, the
  // deepest surface is the deeper navy, and light panels read as the soft panel.
  backgroundColor: IS_GOLF_ELITE ? GE.primary : (HUSTLER ? '#0A0A0A' : '#123D26'),
  canopyColor: IS_GOLF_ELITE ? GE.primaryDeep : (HUSTLER ? '#0A0A0A' : '#123D26'),
  // Cream off-white used by the landing rule panels + admin content area.
  // Premium full build (yesnetlabs) uses the warm cream to match the 4-Ball theme.
  creamColor: IS_GOLF_ELITE ? GE.panel : (HUSTLER ? '#FFFFFF' : ((PBCC_PREMIUM && !FOURBALL_ONLY) ? '#F7F2E6' : '#FFFFFF')),

  // Course (default; per-tournament overrides via tournament.info.pars)
  course: {
    name: IS_GOLF_ELITE ? GE.name : 'Pine Bluff Country Club',
    pars: [5, 3, 4, 4, 3, 4, 4, 5, 3, 4, 5, 4, 3, 5, 4, 3, 4, 4],
    totalPar: 71,
  },

  // Optional course-map image served from /public. Leave null to hide the
  // 🗺️ Course Map button on the score screen. (Golf Elite is brand-neutral —
  // no club course art — so these are null and no PBCC image leaks through.)
  courseMapImage: IS_GOLF_ELITE ? null : '/pbcccourse_transparent.png',

  // Legacy aerial tile (kept only as a decorative hero fallback). NOT the
  // GPS map anymore — see gpsCalibrationImage below.
  gpsSatelliteImage: IS_GOLF_ELITE ? null : '/images/pbcc-satellite.png',

  // THE calibrated GPS course map. Single source of truth: this is the exact
  // image the GPS Calibration tab (/map?calibrate=true) renders, and Mark's
  // on-course drag-calibration (saved at /config/gpsCorrection) was verified
  // against it. Every GPS/course-map surface EXCEPT the home page renders this
  // image at aspect 1680/1048 with objectFit:fill and projects dots through
  // computeCorrectedBounds(correction) — so all screens match the tab exactly.
  // Do NOT swap this without re-running the on-course calibration.
  gpsCalibrationImage: '/images/PBCCGPS1.png',
  gpsCalibrationAspect: '1680 / 1048',

  // Landing page hero — aerial/course photo behind the dark-green gradient
  // overlay. Swap this single line to rebrand the landing page for any
  // club; falls back to courseMapImage when null.
  heroImage: (IS_GOLF_ELITE || HUSTLER) ? null : '/pbcc-aerial.jpg',

  // Rules of Play — bullets rendered in the left cream panel on the
  // landing page. Mirrors the signage posted at the first tee.
  rulesOfPlay: IS_GOLF_ELITE
    ? [
        'USGA Rules of Golf govern all play.',
        'Penalty areas are designated by red or yellow lines and/or stakes.',
        'Out of bounds is defined by white stakes.',
        'Yardage markers are measured to the center of the green.',
      ]
    : [
        'USGA Rules of Golf govern all play.',
        'Penalty areas are designated by red or yellow lines and/or stakes.',
        'Out of bounds is defined by white stakes.',
        'The tennis courts on #9 are out of bounds.',
        'The parking lot on #9 and #18 are out of bounds.',
        'Yardage markers are measured to the center of the green.',
      ],

  // Club Rules — bullets rendered in the right cream panel on the
  // landing page. Mirrors the course-wide policy signage.
  clubRules: IS_GOLF_ELITE
    ? [
        'Observe daily cart rules.',
        'Repair all ball marks, replace all divots, and rake all bunkers.',
        'Allow faster players to play through.',
      ]
    : [
        'Observe daily cart rules.',
        'Pine Bluff CC is a softspike-only facility; metal and ceramic spikes are not permitted.',
        'Repair all ball marks, replace all divots with sand, and rake all bunkers.',
        'Play is limited to sixsomes or less.',
        'Allow faster players to play through.',
        'All play must begin on #1 unless cleared by the golf shop.',
      ],

  // Admin
  defaultPin: IS_GOLF_ELITE ? '1234' : '1996',

  // Tournament limits — informational caps for the admin UI. Setting either
  // to null removes the cap entirely.
  maxFlights: 20,
  maxTeamsPerFlight: null,

  // Footer flag — set false for a club-owned deployment that hides the
  // "Powered by CompleteTourneyLive" badge.
  showPoweredBy: false,
}

export default CLUB
